<template>
  <!-- Full Screen Wrapper -->
  <div class="min-h-screen w-full bg-white flex flex-col md:flex-row font-sans overflow-x-hidden">

    <!-- ================= COLONNE GAUCHE : FORMULAIRES (FULL HEIGHT) ================= -->
    <div class="w-full md:w-1/2 min-h-screen p-8 sm:p-14 lg:p-20 flex flex-col justify-between bg-white z-10">
      
      <!-- Logo Medialink & Branding En-tête -->
      <div>
        <div class="flex items-center gap-3 mb-12">
          <!-- Logo Onde Bleue -->
          <img src="../assets/gemini-svg.svg" alt="Medialink Solutions" class="h-10 w-auto object-contain" />
          <span class="text-xl font-extrabold tracking-tight text-slate-900">
            Medialink <span class="text-blue-600">Solutions</span>
          </span>
        </div>

        <div class="max-w-md">
          <h1 class="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            {{ showReset ? 'Réinitialisation' : 'Espace Officine' }}
          </h1>
          <p class="text-slate-500 font-medium text-sm sm:text-base mt-2">
            {{ showReset ? 'Saisissez vos identifiants pour réinitialiser le mot de passe' : 'Connectez-vous pour gérer vos tickets et files d\'attente' }}
          </p>
        </div>
      </div>

      <!-- Zone Centrale Formulaires -->
      <div class="my-auto py-8 max-w-md w-full">

        <!-- FORMULAIRE LOGIN -->
        <form v-if="!showReset" @submit.prevent="handleLogin" novalidate class="space-y-6">
          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Email Professionnel
            </label>
            <div class="relative">
              <span class="absolute left-4 inset-y-0 flex items-center text-slate-400">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </span>
              <input 
                v-model="email"
                type="email" 
                placeholder="nom@pharmacie.com"
                class="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 font-medium text-sm transition-all"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Mot de passe
            </label>
            <div class="relative">
              <span class="absolute left-4 inset-y-0 flex items-center text-slate-400">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </span>
              <input 
                v-model="password"
                type="password" 
                placeholder="••••••••"
                class="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 font-medium text-sm transition-all"
              />
            </div>
          </div>

          <div class="flex items-center justify-end">
            <button 
              type="button" 
              @click="showReset = true" 
              class="text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors"
            >
              Mot de passe oublié ?
            </button>
          </div>

          <button 
            type="submit" 
            :disabled="isLoading" 
            class="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-sm uppercase tracking-wider rounded-2xl shadow-lg shadow-blue-500/25 transition-all transform active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <span v-if="!isLoading">Se connecter</span>
            <span v-else class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Vérification...
            </span>
          </button>
        </form>

        <!-- FORMULAIRE RESET PASSWORD -->
        <form v-else @submit.prevent="handleResetPassword" novalidate class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Email Professionnel</label>
            <input 
              v-model="resetEmail"
              type="email"
              placeholder="nom@agence.com"
              class="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm transition-all"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Clé de réinitialisation</label>
            <input
              v-model="resetKey"
              type="text"
              placeholder="Clé fournie par le SuperAdmin"
              class="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm transition-all"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Nouveau mot de passe</label>
            <input
              v-model="newPassword"
              type="password"
              placeholder="••••••••"
              class="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm transition-all"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Confirmer le mot de passe</label>
            <input
              v-model="confirmPassword"
              type="password"
              placeholder="••••••••"
              class="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm transition-all"
            />
          </div>

          <div class="pt-2 space-y-3">
            <button 
              type="submit" 
              :disabled="isLoading" 
              class="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm uppercase tracking-wider rounded-2xl shadow-lg shadow-blue-500/25 transition-all disabled:opacity-50 flex items-center justify-center"
            >
              <span v-if="!isLoading">Réinitialiser le mot de passe</span>
              <span v-else class="flex items-center justify-center gap-2">
                <svg class="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Vérification...
              </span>
            </button>

            <button 
              type="button" 
              @click="showReset = false" 
              class="w-full py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors"
            >
              ← Retour à la connexion
            </button>
          </div>
        </form>

      </div>

      <!-- Footer bas de page -->
      <div class="pt-6 border-t border-slate-100 max-w-md">
        <p class="text-xs text-slate-400 font-medium">
          Problème d'accès ? 
          <a href="#" class="font-bold text-blue-600 hover:underline ml-1">Contactez le SuperAdmin</a>
        </p>
      </div>

    </div>

    <!-- ================= COLONNE DROITE : VISUEL PLEIN ÉCRAN ================= -->
    <div class="w-full md:w-1/2 min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-100 p-8 sm:p-14 lg:p-20 flex flex-col justify-between relative overflow-hidden">
      
      <!-- Effet vague décorative en arrière-plan avec l'image SVG -->
      <div class="absolute inset-0 opacity-20 pointer-events-none flex items-center justify-center">
        <img src="../assets/gemini-svg.svg" alt="" class="w-full scale-150 object-cover" />
      </div>

      <!-- Header Droite -->
      <div class="relative z-10 text-center md:text-left">
        <span class="bg-blue-600/10 text-blue-700 text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full">
          Système File d'Attente Pharmacie
        </span>
      </div>

      <!-- Ticket & Visuel Central -->
      <div class="my-auto py-12 relative z-10 flex justify-center">
        <div class="w-full max-w-sm">
          
          <!-- Carte Ticket -->
          <div class="bg-white/90 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/60">
            <div class="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
              <div class="flex items-center gap-2">
                <span class="relative flex h-3 w-3">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </span>
                <span class="text-xs font-bold text-slate-700 uppercase tracking-wider">Guichet Actif</span>
              </div>
              <span class="text-xs font-extrabold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">Medialink</span>
            </div>

            <div class="text-center py-4">
              <p class="text-xs text-slate-400 font-bold uppercase tracking-widest">Numéro en cours</p>
              <p class="text-5xl font-black text-blue-600 tracking-tight my-2">A - 042</p>
              <p class="text-xs text-slate-500 font-medium">Ordonnances &amp; Conseil Pharmacie</p>
            </div>

            <div class="mt-6 pt-4 border-t border-slate-100 flex justify-between items-center text-xs">
              <span class="text-slate-400 font-medium">Temps d'attente estimé</span>
              <span class="font-black text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg">~ 2 min</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Bas de colonne droite -->
      <div class="relative z-10 text-center">
        <h3 class="text-xl font-extrabold text-slate-900 tracking-tight">
          Gestion fluide et ordonnée de vos patients
        </h3>
        <p class="text-xs text-slate-600 max-w-sm mx-auto mt-2 leading-relaxed">
          Suivi des numéros d'appel en temps réel pour une expérience fluide en officine.
        </p>
      </div>

    </div>

  </div>
</template>

<script>
import { toast } from 'vue3-toastify';

export default {
  data() {
    return {
      email: '',
      password: '',
      isLoading: false,
      showReset: false,
      resetEmail: '',
      resetKey: '',
      newPassword: '',
      confirmPassword: ''
    }
  },
  methods: {
    // === LOGIN ===
    async handleLogin() {
      this.isLoading = true;
      try {
        const response = await fetch("https://api.datafuse.fr/api/agency/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: this.email,
            password: this.password
          })
        });

        const data = await response.json();

        if (response.ok) {
          // Stocker les informations de l'utilisateur
          localStorage.setItem("token", data.token);
          localStorage.setItem("userToken", data.token); // Compatibilité
          localStorage.setItem("agencyId", data.user.agencyId);
          localStorage.setItem("userId", data.user.id);
          localStorage.setItem("userRole", data.user.role);
          localStorage.setItem("userName", `${data.user.firstName} ${data.user.lastName}`);
          localStorage.setItem("userData", JSON.stringify(data.user));

          console.log("✅ Login successful - agencyId:", data.user.agencyId);

          toast.success(`Heureux de vous revoir, ${data.user.firstName} !`);

          setTimeout(() => {
            if (data.user.role === "AGENCY_ADMIN") {
              this.$router.push("/admin/dashboard");
            } else {
              this.$router.push("/user-client");
            }
          }, 1000);

        } else {
          toast.error(data.error || "Une erreur est survenue");
        }
      } catch (error) {
        toast.error("Impossible de joindre le serveur de connexion");
        console.error("Login Error:", error);
      } finally {
        this.isLoading = false;
      }
    },

    // === RESET PASSWORD ===
    async handleResetPassword() {
      if (!this.resetEmail || !this.resetKey || !this.newPassword || !this.confirmPassword) {
        toast.error("Tous les champs sont obligatoires !");
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        toast.error("Les mots de passe ne correspondent pas !");
        return;
      }

      this.isLoading = true;
      try {
        const res = await fetch("https://api.datafuse.fr/api/agency/reset-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: this.resetEmail,
            resetKey: this.resetKey,
            newPassword: this.newPassword,
            confirmPassword: this.confirmPassword 
          })
        });

        const data = await res.json();

        if (res.ok) {
          toast.success("Mot de passe réinitialisé avec succès !");
          this.resetEmail = '';
          this.resetKey = '';
          this.newPassword = '';
          this.confirmPassword = '';
          this.showReset = false;
        } else {
          toast.error(data.error || "Erreur lors de la réinitialisation");
        }
      } catch (err) {
        console.error(err);
        toast.error("Impossible de joindre le serveur");
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>